//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoSet } from 'pojo-sets';

import * as galleriesApi from '@apis/galleries/galleriesApi';
import { IGallery } from '@apis/galleries/galleriesApiModels';
import { GalleriesTrackingIds } from '@galleries/constants/galleriesConstants';
import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import getSelectedGallery from '@galleries/selectors/getSelectedGallery';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import ReelId from '@models/ReelId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';

import { galleriesDataActions } from './galleriesDataSlice';

export enum ExistingItemsPublishOptions {
  publishWithoutRights = 'publish-without-rights',
  unpublishItems = 'unpublish-items',
}

export const ExistingItemsPublishOptionList = [
  {
    value: ExistingItemsPublishOptions.publishWithoutRights,
    label: 'Publish Without Rights',
    trackingId:
      GalleriesTrackingIds.RIGHTS_CONFIGURATION_MODAL_PUBLISH_WITHOUT_RIGHTS,
  },
  {
    value: ExistingItemsPublishOptions.unpublishItems,
    label: 'Unpublish Items',
    trackingId: GalleriesTrackingIds.RIGHTS_CONFIGURATION_MODAL_UNPUBLISH,
  },
];

export enum ShouldRequireRights {
  requireRights = 'require-rights',
  dontRequireRights = 'dont-require-rights',
}

interface IContentRightsModalState {
  isOpen: boolean;
  existingItemsPublishOption: ExistingItemsPublishOptions;
  shouldRequireRights: ShouldRequireRights;
  shouldApplyToAllGalleries: boolean;
}

const initialState: IContentRightsModalState = {
  isOpen: false,
  existingItemsPublishOption: ExistingItemsPublishOptions.publishWithoutRights,
  shouldRequireRights: ShouldRequireRights.requireRights,
  shouldApplyToAllGalleries: false,
};

const { actions, caseReducers, reducer } = createSlice({
  name: SliceNames.GalleriesConfigureRightsModal,
  initialState,
  reducers: {
    openModal: (state) => {
      state.isOpen = true;
    },
    closeModal: (state, action: PayloadAction<IGallery>) => {
      state.isOpen = false;
      state.existingItemsPublishOption =
        initialState.existingItemsPublishOption;
      state.shouldRequireRights = action.payload.isRightsManaged
        ? ShouldRequireRights.requireRights
        : ShouldRequireRights.dontRequireRights;
      state.shouldApplyToAllGalleries = false;
    },
    setExistingItemsPublishOption: (
      state,
      action: PayloadAction<ExistingItemsPublishOptions>,
    ) => {
      state.existingItemsPublishOption = action.payload;
    },
    setShouldRequireRights: (
      state,
      action: PayloadAction<ShouldRequireRights>,
    ) => {
      state.shouldRequireRights = action.payload;
    },
    setShouldApplyToAllGalleries: (state, action: PayloadAction<boolean>) => {
      state.shouldApplyToAllGalleries = action.payload;
    },
  },
});

export const contentRightsModalCaseReducers = caseReducers;

export const successToast: IToastNotification = {
  title: 'Settings Saved',
  type: ToastType.SUCCESS,
  message: "We've successfully updated your Content Rights settings.",
  options: {
    toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
  },
};

export default reducer;

export const contentRightsModalActions = {
  ...actions,

  closeModal: createThunk(() => (dispatch, getState) => {
    const selectedGallery = getSelectedGallery(getState());
    isDefined(selectedGallery) && dispatch(actions.closeModal(selectedGallery));
  }),

  /**
   * @param isRecursiveCall: A boolean to determine whether or not this is a recursive call.
   *  This parameter is set to true for all calls made recursively, and false for all calls made externally.
   *  This distinction is important because a response of 'false' (i.e. a skip rights job isn't running) should
   *  only trigger 'job completion' logic if a previous response was 'true'. In other words, if the job
   *  was determined to be running and is now no longer running.
   *
   * @param reelId: The reel that we're currently checking.
   */
  pollForJobStatus: createThunk(
    (reelId: ReelId, isRecursiveCall: boolean = false) =>
      async (dispatch, getState) => {
        const isSkippingRightsReels =
          getState().galleries.data.flags.isSkippingRightsOnItems;
        const wasSkippingRightsOnReel = PojoSet.has(
          isSkippingRightsReels,
          reelId,
        );
        const currentlySkippingRightsOnReel =
          await galleriesApi.isSkipRightsRunning(reelId);

        // We were running a skip rights job that finished
        const skipRightsJobEnded =
          wasSkippingRightsOnReel && !currentlySkippingRightsOnReel;
        const stillSkippingRightsInThisRecursionChain =
          wasSkippingRightsOnReel &&
          currentlySkippingRightsOnReel &&
          isRecursiveCall;
        const skipRightsJobStarted =
          !wasSkippingRightsOnReel && currentlySkippingRightsOnReel;

        if (skipRightsJobEnded) {
          const currentlySelectedReelid =
            getSelectedGallery(getState())?.reelId;

          // The gallery that we're looking at just finished a skip-rights job, update the gallery items.
          if (currentlySelectedReelid === reelId) {
            void dispatch(galleriesDataActions.getGalleryItems());
          }

          dispatch(
            galleriesDataActions.setIsSkippingRights(
              PojoSet.remove(isSkippingRightsReels, reelId),
            ),
          );
        } else if (
          stillSkippingRightsInThisRecursionChain ||
          skipRightsJobStarted
        ) {
          dispatch(
            galleriesDataActions.setIsSkippingRights(
              PojoSet.add(isSkippingRightsReels, reelId),
            ),
          );
          return new Promise((resolve) => {
            setTimeout(
              () =>
                resolve(
                  dispatch(
                    contentRightsModalActions.pollForJobStatus(reelId, true),
                  ),
                ),
              3000,
            );
          });
        }
      },
  ),

  save: createThunk(() => async (dispatch, getState) => {
    const {
      existingItemsPublishOption,
      shouldApplyToAllGalleries,
      shouldRequireRights,
    } = getState().galleries.contentRightsModal;
    const selectedGallery = getSelectedGallery(getState());
    const allGalleries = getAllAlphabetizedGalleries(getState());

    const setIsRightsManaged =
      shouldRequireRights === ShouldRequireRights.requireRights;

    const galleriesToUpdate = (() => {
      if (shouldApplyToAllGalleries) {
        return allGalleries.filter(
          (gallery) => gallery.isRightsManaged !== setIsRightsManaged,
        );
      } else if (
        selectedGallery &&
        selectedGallery.isRightsManaged !== setIsRightsManaged
      ) {
        return [selectedGallery];
      } else {
        return [];
      }
    })();
    const reelIds = galleriesToUpdate.map((gallery) => gallery.reelId);

    const updatedReels = await (reelIds.length > 0
      ? galleriesApi.setRightsManagement({
          reelIds,
          isRightsManaged: setIsRightsManaged,
          skipRightsOnApprovedItems:
            setIsRightsManaged &&
            existingItemsPublishOption ===
              ExistingItemsPublishOptions.publishWithoutRights,
        })
      : Promise.resolve([])); // no-op if there are no reels to update

    updatedReels.forEach((reel) => {
      const originalGallery = galleriesToUpdate.find(
        (gallery) => gallery.reelId === reel.id,
      );
      // Update isRightsManaged from the request rather than the response
      //  if we're skipping rights on a reel, we won't update rights management settings until
      //  the job is finished, but the gallery still needs to be recognized as rights managed
      //  from the dashboard's perspective
      if (originalGallery) {
        const modifiedGallery = {
          ...originalGallery,
          isRightsManaged: setIsRightsManaged,
        };
        dispatch(galleriesDataActions.receiveGallery(modifiedGallery));

        // Is this gallery the currently-selected gallery
        if (selectedGallery && reel.id === selectedGallery.reelId) {
          dispatch(actions.closeModal(modifiedGallery));

          // Made a request that wasn't a no-op fo the currently-visible gallery.
          //   Re-fetch its items.
          void dispatch(galleriesDataActions.getGalleryItems());

          // We started a skip rights job on the currently-visible gallery. Show the loading state
          //  and start polling.
          if (
            existingItemsPublishOption ===
              ExistingItemsPublishOptions.publishWithoutRights &&
            setIsRightsManaged
          ) {
            void dispatch(
              contentRightsModalActions.pollForJobStatus(
                selectedGallery.reelId,
              ),
            );
          }
        }
      }
    });

    // We made a change to at least one gallery, show a success toast
    if (updatedReels.length > 0) {
      dispatch(toastNotificationsActions.addToastNotification(successToast));
    }
  }),
};
